/**
 * Enterprise Premium Animation Utilities
 * - Smooth, professional animations
 * - Short durations (200-400ms)
 * - Subtle movements (10-20px)
 * - No bounce or exaggerated effects
 */

import { Variants, Transition } from 'framer-motion';

// ========== Duration Tokens ==========
export const DURATION = {
  instant: 0.1,     // hover states
  fast: 0.2,        // micro-interactions
  normal: 0.35,     // standard transitions
  slow: 0.5,        // page transitions
} as const;

// ========== Easing Curves ==========
export const EASE = {
  smooth: [0.4, 0, 0.2, 1] as const,     // default - smooth deceleration
  out: [0, 0, 0.2, 1] as const,          // exit animations
  in: [0.4, 0, 1, 1] as const,           // enter animations
  inOut: [0.4, 0, 0.2, 1] as const,      // symmetric transitions
} as const;

// ========== Base Transition ==========
export const baseTransition: Transition = {
  duration: DURATION.normal,
  ease: EASE.smooth,
};

// ========== Fade Variants ==========
export const fadeIn: Variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: DURATION.normal }
  },
  exit: {
    opacity: 0,
    transition: { duration: DURATION.fast }
  },
};

export const fadeInUp: Variants = {
  initial: {
    opacity: 0,
    y: 15
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: DURATION.fast,
    }
  },
};

export const fadeInDown: Variants = {
  initial: {
    opacity: 0,
    y: -15
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
};

export const fadeInLeft: Variants = {
  initial: {
    opacity: 0,
    x: -20
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
};

export const fadeInRight: Variants = {
  initial: {
    opacity: 0,
    x: 20
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
};

// ========== Scale Variants ==========
export const fadeInScale: Variants = {
  initial: {
    opacity: 0,
    scale: 0.97
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    transition: {
      duration: DURATION.fast,
    }
  },
};

export const scaleIn: Variants = {
  initial: {
    scale: 0.95,
    opacity: 0,
  },
  animate: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
};

// ========== Stagger Containers ==========
export const staggerContainer: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

export const staggerContainerFast: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
};

export const staggerContainerSlow: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.15,
    },
  },
};

// ========== Scroll-Triggered Sections ==========
export const sectionReveal: Variants = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.slow,
      ease: EASE.smooth,
    }
  },
};

// Viewport options for scroll animations
export const viewportOnce = {
  once: true,
  margin: "-50px" as const,
};

export const viewportAlways = {
  once: false,
  margin: "-100px" as const,
};

// ========== Card Animations ==========
export const cardHover = {
  rest: {
    y: 0,
    transition: {
      duration: DURATION.fast,
      ease: EASE.smooth,
    }
  },
  hover: {
    y: -4,
    transition: {
      duration: DURATION.fast,
      ease: EASE.smooth,
    }
  },
};

// ========== Button Animations ==========
export const buttonTap = {
  tap: {
    scale: 0.98,
    transition: {
      duration: DURATION.instant,
    }
  },
};

// ========== Navigation Animations ==========
export const navItemHover = {
  rest: {
    opacity: 0.8,
  },
  hover: {
    opacity: 1,
    transition: {
      duration: DURATION.instant,
    }
  },
};

export const mobileMenuSlide: Variants = {
  initial: {
    x: '100%',
    opacity: 0,
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: {
      duration: DURATION.fast,
      ease: EASE.in,
    }
  },
};

// ========== Modal/Dialog Animations ==========
export const modalOverlay: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: DURATION.fast,
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: DURATION.fast,
    }
  },
};

export const modalContent: Variants = {
  initial: {
    opacity: 0,
    scale: 0.96,
    y: 10,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    y: 10,
    transition: {
      duration: DURATION.fast,
    }
  },
};

// ========== Text Reveal ==========
export const textReveal: Variants = {
  initial: {
    opacity: 0,
    y: 10,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
};

// ========== List Item Animation ==========
export const listItem: Variants = {
  initial: {
    opacity: 0,
    x: -10
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASE.smooth,
    }
  },
};

// ========== Counter Animation Helper ==========
export const counterAnimation = (value: number, duration: number = 2) => ({
  initial: { count: 0 },
  animate: {
    count: value,
    transition: {
      duration,
      ease: EASE.smooth,
    }
  },
});

// ========== Reduced Motion Support ==========
export const reducedMotionVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

// Helper to check for reduced motion preference
export const prefersReducedMotion =
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

// Get appropriate variants based on motion preference
export const getMotionVariants = (variants: Variants): Variants => {
  return prefersReducedMotion ? reducedMotionVariants : variants;
};
